import type {
  AccountListParams,
  ConfirmUploadAccountParams,
  ConfirmUploadAccountResponse,
  QueryUploadAccountPreviewListParams,
  UpdateApplovinTokenParams,
  UploadAccountPreviewParams,
  UploadAccountPreviewResponse,
} from './types'

import type { PagingRequest } from '#/api/type'

import { defineApi } from '#/api/define-api'
import { EXPORT_TASK_BIZ_CODE, exportTaskApi } from '#/api/modules/common'
import { requestClient } from '#/api/request'

export const accountApi = defineApi('operation', ({ prefix }) => {
  function getAccountListParams(params: PagingRequest<AccountListParams>) {
    return {
      accountId: params.condition.accountId,
      accountName: params.condition.accountName,
      mediaAccountStatus: params.condition?.mediaAccountStatus?.map(Number),
      currency: params.condition.currency,
      agency: params.condition.agency,
      accountType: params.condition.accountType,
      mediaPlatform: params.condition.mediaPlatform,
    }
  }
  // adsAccount/query
  function getAccountList(params: PagingRequest<AccountListParams>) {
    params.condition = getAccountListParams(params)
    params = {
      ...params,
      condition: getAccountListParams(params),
    }
    return requestClient
      .post(prefix('adsAccount/query'), params)
      .then((res) => {
        return {
          items: res?.list || [],
          total: res?.total || 0,
        }
      })
  }

  function exportAccountList(params: AccountListParams) {
    return exportTaskApi.submitExportTask(
      EXPORT_TASK_BIZ_CODE.ADS_ACCOUNT_EXPORT,
      {
        condition: params,
      },
    )
  }

  function editAgency(params: { ids: string[]; agency: string }) {
    return requestClient.post(prefix('adsAccount/editAgency'), params)
  }

  function editAccountType(params: { ids: number[]; accountType: string }) {
    return requestClient.post(prefix('adsAccount/editAccountType'), params)
  }

  function editAccountName(params: { accountName: string; id: number }) {
    return requestClient.post(prefix('adsAccount/editAccountName'), params)
  }

  function updateApplovinToken(params: UpdateApplovinTokenParams) {
    return requestClient.post(prefix('adsAccount/updateApplovinToken'), params)
  }

  /**
   * 下载广告账号导入模版
   * GET /gobestads/operation/adsAccount/downloadTemplate
   */
  function downloadTemplate() {
    return requestClient
      .get(prefix('adsAccount/downloadTemplate'))
      .then((res) => {
        return res?.url || ''
      })
  }

  /**
   * 上传广告账号预览
   * POST /gobestads/operation/adsAccount/uploadPreview
   */
  function uploadAccountPreview(params: UploadAccountPreviewParams) {
    const formData = new FormData()
    formData.append('file', params.file)
    formData.append('mediaPlatform', params.mediaPlatform)

    return requestClient
      .post<UploadAccountPreviewResponse>(
        prefix('adsAccount/uploadPreview'),
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      .then((res) => {
        return {
          totalCount: res?.totalCount || 0,
          successCount: res?.successCount || 0,
          failCount: res?.failCount || 0,
          uploadId: res?.uploadId || '',
        }
      })
  }

  /**
   * 查询上传广告账号列表
   * POST /gobestads/operation/adsAccount/queryUploadPreviewList
   */
  function queryUploadAccountPreviewList(
    params: QueryUploadAccountPreviewListParams,
  ) {
    return requestClient
      .post(prefix('adsAccount/queryUploadPreviewList'), {
        condition: {
          uploadId: params.condition.uploadId,
        },
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
      })
      .then((res) => {
        return {
          list: res?.list || [],
          total: res?.total || 0,
        }
      })
  }

  /**
   * 确认上传广告账号
   * POST /gobestads/operation/adsAccount/confirmUpload
   */
  function confirmUploadAccount(params: ConfirmUploadAccountParams) {
    return requestClient
      .post<ConfirmUploadAccountResponse>(prefix('adsAccount/confirmUpload'), {
        uploadId: params.uploadId,
        agency: params.agency,
      })
      .then((res) => {
        return {
          successCount: res?.successCount || 0,
          totalCount: res?.totalCount || 0,
        }
      })
  }

  return {
    getAccountList,
    exportAccountList,
    editAgency,
    editAccountName,
    editAccountType,
    updateApplovinToken,
    getAccountListParams,
    downloadTemplate,
    uploadAccountPreview,
    queryUploadAccountPreviewList,
    confirmUploadAccount,
  }
})
